/**
 * External image domains that are blocked by Cross-Origin-Resource-Policy
 * and cannot be loaded in the browser. URLs from these domains return null
 * so the UI shows a placeholder instead of a broken image.
 */
const BLOCKED_IMAGE_DOMAINS = [
  'cdn.novelupdates.com',
  'www.novelupdates.com',
  'novelupdates.com',
];

const isBlockedImageDomain = (url) => {
  if (!url || typeof url !== 'string') return false;
  try {
    const parsed = new URL(url, window.location.origin);
    return BLOCKED_IMAGE_DOMAINS.some(
      (domain) => parsed.hostname === domain || parsed.hostname.endsWith(`.${domain}`),
    );
  } catch {
    return false;
  }
};

/**
 * Get the best available image URL for a novel.
 *
 * Covers are stored locally and served from our own /api/novels/{id}/image
 * endpoint — external image URLs are no longer accepted for new novels.
 *
 * However, a one-time backend migration (NovelCoverUrlMigrator) downloads
 * existing external coverImageUrl values into local storage. Until that
 * migration completes for a given novel (or if the download fails and is
 * retried on the next boot), the novel may still have an external
 * coverImageUrl and no stored image. This fallback ensures those novels
 * still display their cover during the migration window. Once the migrator
 * successfully processes a novel, coverImageUrl is cleared and only the
 * stored image is used — so this fallback stops triggering automatically.
 *
 * @param {Object} novel - The novel object from the API
 * @returns {string|null} The image URL to use, or null if no image available
 */
export const getNovelImageUrl = (novel) => {
  if (!novel) {
    return null;
  }

  // Priority 1: locally-stored images (the post-migration source of truth).
  if (novel.hasStoredImage && novel.storedImageUrl) {
    const imageUrl = novel.storedImageUrl;

    // If it's already a full URL, use it as-is (unless from a blocked domain)
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return isBlockedImageDomain(imageUrl) ? null : imageUrl;
    }

    // If it's a relative URL, prepend the current origin
    // In development: proxied through React dev server (localhost:3000 → localhost:8081)
    // In production: same origin as the frontend
    if (imageUrl.startsWith('/')) {
      return `${window.location.origin}${imageUrl}`;
    }

    return imageUrl;
  }

  // Priority 2 (migration fallback): if no stored image but an external
  // coverImageUrl still exists, use it. This covers novels the migrator
  // hasn't processed yet (or failed to download and will retry next boot).
  // Once the migrator clears the URL, this fallback stops triggering.
  if (novel.coverImageUrl) {
    return isBlockedImageDomain(novel.coverImageUrl) ? null : novel.coverImageUrl;
  }

  // No image at all — return null so the UI shows a placeholder.
  return null;
};

/**
 * Get image metadata for a novel
 * 
 * @param {Object} novel - The novel object from the API
 * @returns {Object} Image metadata
 */
export const getNovelImageMetadata = (novel) => {
  if (!novel) {
    return {
      isStored: false,
      url: null,
      fileSize: null,
    };
  }

  return {
    isStored: novel.hasStoredImage || false,
    url: getNovelImageUrl(novel),
    fileSize: novel.imageFileSize || null,
  };
};
